import {
    useEffect,
    useState } from "react";
import { Link,
    useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import {
    adminListProjects,
    adminDeleteProject,
    adminMe,
    adminReorderProjects,
} from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

export default function AdminProjects() {
    const [projects, setProjects] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    const load = () =>
        adminListProjects()
            .then(setProjects)
            .catch((err) => setError(err?.response?.data?.detail || "Could not load projects"));

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (slug, title) => {
        const ok = window.confirm(`Delete "${title}"? This cannot be undone.`);
        if (!ok) return;
        try {
            setBusy(true);
            await adminDeleteProject(slug);
            await load();
        } catch (err) {
            setError(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    const move = async (idx, dir) => {
        if (!projects) return;
        const j = idx + dir;
        if (j < 0 || j >= projects.length) return;
        const next = [...projects];
        [next[idx], next[j]] = [next[j], next[idx]];
        setProjects(next);
        try {
            await adminReorderProjects(next.map((p) => p.slug));
        } catch (err) {
            setError("Reorder failed; reloading.");
            load();
        }
    };

    return (
        <main data-testid="page-admin-projects" className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24">
            <Seo path="/admin/projects" title="Projects — Admin" description="Manage projects" />
            <AdminHeader title="Projects" />
            <div className="mt-8 flex justify-end">
                <Link
                    to="/admin/projects/new"
                    data-testid="admin-new-project"
                    className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#1A1A1A] text-[#F9F8F6] hover:bg-black"
                >
                    + New project
                </Link>
            </div>

            {error && <p className="mt-8 text-red-700 text-sm" data-testid="admin-projects-error">{error}</p>}

            {projects == null ? (
                <p className="mt-12 asc-overline" data-testid="admin-projects-loading">Loading…</p>
            ) : projects.length === 0 ? (
                <p className="mt-12 text-[#5C5855]" data-testid="admin-projects-empty">
                    No projects yet. <Link to="/admin/projects/new" className="underline">Create one</Link>.
                </p>
            ) : (
                <div className="mt-10 overflow-x-auto">
                    <table data-testid="admin-projects-table" className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] border-b border-[#E5E1DB]">
                                <th className="py-3 pr-4 w-12">#</th>
                                <th className="py-3 pr-4 w-16">Cover</th>
                                <th className="py-3 pr-4">Title</th>
                                <th className="py-3 pr-4">Slug</th>
                                <th className="py-3 pr-4">Category</th>
                                <th className="py-3 pr-4">Year</th>
                                <th className="py-3 pr-4 w-32">Order</th>
                                <th className="py-3 pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((p, idx) => (
                                <tr
                                    key={p.slug}
                                    data-testid={`admin-project-row-${p.slug}`}
                                    className="border-b border-[#E5E1DB] hover:bg-[#F2EFEB] align-middle"
                                >
                                    <td className="py-3 pr-4 text-[#8C7A6B]">{idx + 1}</td>
                                    <td className="py-3 pr-4">
                                        {p.image && (
                                            <img
                                                src={p.image}
                                                alt=""
                                                className="w-12 h-12 object-cover rounded"
                                            />
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 text-[#1A1A1A] font-medium">{p.title}</td>
                                    <td className="py-3 pr-4 text-[#5C5855] font-mono text-xs">{p.slug}</td>
                                    <td className="py-3 pr-4 text-[#5C5855]">{p.category}</td>
                                    <td className="py-3 pr-4 text-[#5C5855]">{p.year}</td>
                                    <td className="py-3 pr-4">
                                        <button
                                            onClick={() => move(idx, -1)}
                                            disabled={idx === 0 || busy}
                                            data-testid={`move-up-${p.slug}`}
                                            className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30"
                                        >
                                            ↑
                                        </button>
                                        <button
                                            onClick={() => move(idx, 1)}
                                            disabled={idx === projects.length - 1 || busy}
                                            data-testid={`move-down-${p.slug}`}
                                            className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#5C5855] hover:text-[#1A1A1A] disabled:opacity-30"
                                        >
                                            ↓
                                        </button>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <a
                                            href={`/projects/${p.slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`view-${p.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#5C5855] hover:text-[#1A1A1A] mr-4"
                                        >
                                            View
                                        </a>
                                        <Link
                                            to={`/admin/projects/${p.slug}/edit`}
                                            data-testid={`edit-${p.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] mr-4"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            onClick={() => onDelete(p.slug, p.title)}
                                            disabled={busy}
                                            data-testid={`delete-${p.slug}`}
                                            className="text-[10px] uppercase tracking-[0.2em] text-red-700 hover:text-red-900 disabled:opacity-40"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </main>
    );
}
